<script setup>
import { ref, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MediaCard from './MediaCard.vue';

const props = defineProps({
  mediaItems: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add', 'edit', 'delete', 'view']);

// Group media by category
const groupedMedia = computed(() => {
  const groups = {};
  props.mediaItems.forEach(item => {
    const category = item.category || 'Uncategorized';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
  });
  return groups;
});

const categoryLabels = {
  usp: 'USP (Unique Selling Points)',
  benefits: 'Benefits',
  pain_points: 'Pain Points',
  features: 'Features',
  how_to_use: 'How to Use',
  ingredients: 'Ingredients',
  uncategorized: 'Uncategorized',
};

const handleAdd = () => {
  emit('add');
};

const handleEdit = item => {
  emit('edit', item);
};

const handleDelete = item => {
  emit('delete', item);
};

const handleView = item => {
  emit('view', item);
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Add Button -->
    <div class="flex justify-end">
      <Button
        color="primary"
        icon="i-lucide-plus"
        label="Add Media"
        class="!bg-woot-500 hover:!bg-woot-600"
        @click="handleAdd"
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="i-lucide-loader-2 w-8 h-8 text-woot-500 animate-spin" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="mediaItems.length === 0"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <span class="i-lucide-image w-16 h-16 text-n-slate-6 mb-4" />
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        No media files yet
      </h3>
      <p class="text-sm text-n-slate-10 mb-6 max-w-md">
        Upload images and videos to help the AI understand your products better.
      </p>
      <Button
        color="primary"
        icon="i-lucide-upload"
        label="Upload Media"
        class="!bg-woot-500 hover:!bg-woot-600"
        @click="handleAdd"
      />
    </div>

    <!-- Media Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <MediaCard
        v-for="item in mediaItems"
        :key="item.id"
        :media="item"
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
      />
    </div>
  </div>
</template>
